export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: ErrorDetail;
}

export class ApiError extends Error {
    code?: string;
    detail?: ErrorDetail;

    constructor(detail?: ErrorDetail, defaultMessage?: string) {
        const message = detail?.message || (detail?.code ? getErrorMessage(detail.code) : defaultMessage) || 'An error occurred';
        super(message);
        this.name = 'ApiError';
        this.code = detail?.code;
        this.detail = detail;
    }
}

export interface ErrorDetail {
    code: string;
    message?: string;
}

export enum AuthErrorCode {
    INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
    PENDING_USER_ACCOUNT_SETUP = 'PENDING_USER_ACCOUNT_SETUP',
    INACTIVE_USER_ACCOUNT = 'INACTIVE_USER_ACCOUNT',
    EXPIRED_TOKEN = 'EXPIRED_TOKEN',
    INVALID_TOKEN = 'INVALID_TOKEN',
    TOKEN_ALREADY_USED = 'TOKEN_ALREADY_USED',
    TOKEN_INVALIDATED = 'TOKEN_INVALIDATED',
    ILLEGAL_PASSWORD = 'ILLEGAL_PASSWORD',
    PASSWORDS_NOT_MATCHING = 'PASSWORDS_NOT_MATCHING',
    MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
    UNEXPECTED_ERROR = 'UNEXPECTED_ERROR'
}

export enum RequestErrorCode {
    OVERLAPPING_LEAVE_REQUEST = 'OVERLAPPING_LEAVE_REQUEST',
    INSUFFICIENT_BALANCE = 'INSUFFICIENT_BALANCE',
    INVALID_DATE_RANGE = 'INVALID_DATE_RANGE',
    EMPLOYEE_NOT_FOUND = 'EMPLOYEE_NOT_FOUND',
    REQUEST_NOT_FOUND = 'REQUEST_NOT_FOUND',
    REQUEST_ALREADY_APPROVED = 'REQUEST_ALREADY_APPROVED',
    REQUEST_ALREADY_REJECTED = 'REQUEST_ALREADY_REJECTED',
    INVALID_REQUEST_STATE = 'INVALID_REQUEST_STATE',
    MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
    UNAUTHORIZED_ACTION = 'UNAUTHORIZED_ACTION',
    UNEXPECTED_ERROR = 'UNEXPECTED_ERROR'
}

export enum LeaveRequestErrorCode {
    OVERLAPPING_LEAVE_REQUEST = 'OVERLAPPING_LEAVE_REQUEST',
    INSUFFICIENT_BALANCE = 'INSUFFICIENT_BALANCE',
    INVALID_DATE_RANGE = 'INVALID_DATE_RANGE',
    EMPLOYEE_NOT_FOUND = 'EMPLOYEE_NOT_FOUND',
    UNEXPECTED_ERROR = 'UNEXPECTED_ERROR'
}

export enum CommonErrorCode {
    VALIDATION_ERROR = 'VALIDATION_ERROR',
    UNAUTHORIZED = 'UNAUTHORIZED',
    FORBIDDEN = 'FORBIDDEN',
    NOT_FOUND = 'NOT_FOUND',
    CONFLICT = 'CONFLICT',
    INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR'
}

// ============================================================================
// Error Message Mapping
// ============================================================================

type ErrorCodeUnion = AuthErrorCode | RequestErrorCode | CommonErrorCode;

export const ErrorMessages: Record<ErrorCodeUnion, string> = {
    // Auth errors
    [AuthErrorCode.INVALID_CREDENTIALS]:
        'Der Benutzername oder das Passwort ist inkorrekt.',
    [AuthErrorCode.PENDING_USER_ACCOUNT_SETUP]:
        'Bitte schließen Sie die Einrichtung Ihres Kontos über den Link in der erhaltenen E-Mail ab.',
    [AuthErrorCode.INACTIVE_USER_ACCOUNT]:
        'Ihr Benutzerkonto wurde deaktiviert. Bitte kontaktieren Sie Ihren Administrator.',
    [AuthErrorCode.EXPIRED_TOKEN]:
        'Der Einrichtungslink ist abgelaufen.',
    [AuthErrorCode.INVALID_TOKEN]:
        'Der Link ist ungültig oder fehlerhaft.',
    [AuthErrorCode.TOKEN_ALREADY_USED]:
        'Dieser Link wurde bereits verwendet.',
    [AuthErrorCode.TOKEN_INVALIDATED]:
        'Der Einrichtungslink ist nicht mehr gültig, da bereits ein neuer Link angefordert wurde.',
    [AuthErrorCode.ILLEGAL_PASSWORD]:
        'Das Passwort erfüllt nicht die Sicherheitsanforderungen.',
    [AuthErrorCode.PASSWORDS_NOT_MATCHING]:
        'Die eingegebenen Passwörter stimmen nicht überein.',
    [AuthErrorCode.MISSING_REQUIRED_FIELD]:
        'Bitte füllen Sie alle erforderlichen Felder aus.',
    [AuthErrorCode.UNEXPECTED_ERROR]:
        'Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es später erneut.',

    // Request errors
    [RequestErrorCode.OVERLAPPING_LEAVE_REQUEST]:
        'Es existiert bereits ein Urlaubsantrag für diesen Zeitraum.',
    [RequestErrorCode.INSUFFICIENT_BALANCE]:
        'Ihr Urlaubsguthaben reicht nicht aus für den gewählten Zeitraum.',
    [RequestErrorCode.INVALID_DATE_RANGE]:
        'Der angegebene Zeitraum ist ungültig.',
    [RequestErrorCode.EMPLOYEE_NOT_FOUND]:
        'Mitarbeiter wurde nicht gefunden.',
    [RequestErrorCode.REQUEST_NOT_FOUND]:
        'Der Antrag wurde nicht gefunden.',
    [RequestErrorCode.REQUEST_ALREADY_APPROVED]:
        'Dieser Antrag wurde bereits genehmigt.',
    [RequestErrorCode.REQUEST_ALREADY_REJECTED]:
        'Dieser Antrag wurde bereits abgelehnt.',
    [RequestErrorCode.INVALID_REQUEST_STATE]:
        'Der Antrag befindet sich in einem ungültigen Status.',
    [RequestErrorCode.MISSING_REQUIRED_FIELD]:
        'Bitte füllen Sie alle erforderlichen Felder aus.',
    [RequestErrorCode.UNAUTHORIZED_ACTION]:
        'Sie haben keine Berechtigung für diese Aktion.',
    [RequestErrorCode.UNEXPECTED_ERROR]:
        'Es ist ein unerwarteter Fehler aufgetreten.',

    // Common errors
    [CommonErrorCode.VALIDATION_ERROR]:
        'Die eingegebenen Daten sind ungültig.',
    [CommonErrorCode.UNAUTHORIZED]:
        'Sie sind nicht angemeldet. Bitte melden Sie sich an.',
    [CommonErrorCode.FORBIDDEN]:
        'Sie haben keine Berechtigung für diese Aktion.',
    [CommonErrorCode.NOT_FOUND]:
        'Die angeforderte Ressource wurde nicht gefunden.',
    [CommonErrorCode.CONFLICT]:
        'Die Aktion konnte aufgrund eines Konflikts nicht ausgeführt werden.',
    [CommonErrorCode.INTERNAL_SERVER_ERROR]:
        'Ein Serverfehler ist aufgetreten. Bitte versuchen Sie es später erneut.'
};

// ============================================================================
// Utility Functions
// ============================================================================

export function getErrorMessage(errorCode: string | undefined): string {
    if (!errorCode) {
        return ErrorMessages[CommonErrorCode.INTERNAL_SERVER_ERROR];
    }

    return ErrorMessages[errorCode as ErrorCodeUnion] ||
        ErrorMessages[CommonErrorCode.INTERNAL_SERVER_ERROR];
}

export function isAuthError(errorCode: string): boolean {
    return Object.values(AuthErrorCode).includes(errorCode as AuthErrorCode);
}

export function isLeaveRequestError(errorCode: string): boolean {
    return Object.values(LeaveRequestErrorCode).includes(errorCode as LeaveRequestErrorCode);
}